import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router';
import { ArrowLeft, Save01, X, ChevronRight, ChevronLeft, Plus, Trash01, Settings01 } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { TextArea } from '@/components/base/textarea/textarea';
import { Stepper, StepperStep } from '@/components/application/stepper';
import { ModalOverlay, Modal, Dialog } from '@/components/application/modals/modal';
import { LoadingIndicator } from '@/components/application/loading-indicator/loading-indicator';
import { getCompany, createCompanyWithTeam, updateCompany } from '@/data/api/companies';
import { countries } from '@/data/countries';
import type {
    CompanyFormState,
    CompanyFormStep1Data,
    CompanyType,
    EntityType,
    TeamMember,
    UserRole,
} from '@/types/company';
import { useTranslation } from 'react-i18next';

export const CompanyFormPage = () => {
    const { t } = useTranslation(['pages:companies', 'common']);
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEdit = Boolean(id);

    const [formState, setFormState] = useState<CompanyFormState>({
        currentStep: 1,
        isStepValid: { 1: false, 2: true }, // Step 2 is optional
        step1: {
            company_type: '' as CompanyType | '',
            company_name: '',
            legal_name: '',
            entityType: '' as EntityType | '',
            tax_id: '',
            address: {
                street: '',
                city: '',
                state: '',
                postalCode: ''
            },
            country_code: '',
            primary_email: '',
            primary_phone: '',
            is_active: true,
            notes: ''
        },
        step2: {
            team_members: []
        }
    });

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [newMemberEmail, setNewMemberEmail] = useState('');
    const [newMemberFirstName, setNewMemberFirstName] = useState('');
    const [newMemberLastName, setNewMemberLastName] = useState('');
    const [newMemberRole, setNewMemberRole] = useState<UserRole>('operator');

    const companyTypeOptions = [
        { id: 'sender', label: t('form.fields.companyTypes.sender') },
        { id: 'receiver', label: t('form.fields.companyTypes.receiver') },
        { id: 'freight_forwarder', label: t('form.fields.companyTypes.freightForwarder') },
        { id: 'carrier', label: t('form.fields.companyTypes.carrier') },
        { id: 'customs_broker', label: t('form.fields.companyTypes.customsBroker') },
    ];

    const statusOptions = [
        { id: 'true', label: t('list.status.active') },
        { id: 'false', label: t('list.status.inactive') }
    ];

    const countryOptions = countries.map(country => ({
        id: country.id,
        label: country.label
    }));

    const roleOptions = [
        { id: 'operator', label: t('form.stepper.step2.roles.operator') },
        { id: 'company_admin', label: t('form.stepper.step2.roles.companyAdmin') },
    ];

    // Validation functions
    const validateStep1 = (data: CompanyFormStep1Data): boolean => {
        return !!(
            data.company_type &&
            data.company_name.trim() &&
            data.legal_name.trim() &&
            data.tax_id.trim() &&
            data.address.street.trim() &&
            data.address.city.trim() &&
            data.address.state.trim() &&
            data.address.postalCode.trim() &&
            data.country_code &&
            data.primary_email.trim()
        );
    };

    useEffect(() => {
        if (isEdit && id) {
            const loadCompany = async () => {
                setIsLoading(true);
                try {
                    const company = await getCompany(id);
                    const newFormState: CompanyFormState = {
                        currentStep: 1,
                        isStepValid: { 1: false, 2: true },
                        step1: {
                            company_type: company.company_type,
                            company_name: company.company_name,
                            legal_name: company.legal_name || '',
                            entityType: '' as EntityType | '', // Not in backend model
                            tax_id: company.tax_id || '',
                            address: {
                                street: company.headquarters_address?.street || '',
                                city: company.headquarters_address?.city || '',
                                state: company.headquarters_address?.state_province || '',
                                postalCode: company.headquarters_address?.postal_code || ''
                            },
                            country_code: company.headquarters_address?.country_code || '',
                            primary_email: company.primary_email || '',
                            primary_phone: company.primary_phone || '',
                            is_active: company.is_active,
                            notes: company.notes || ''
                        },
                        step2: {
                            team_members: [] // Will be loaded separately if needed
                        }
                    };

                    newFormState.isStepValid[1] = validateStep1(newFormState.step1);
                    setFormState(newFormState);
                } catch (error) {
                    console.error('Error loading company:', error);
                } finally {
                    setIsLoading(false);
                }
            };

            loadCompany();
        }
    }, [isEdit, id]);

    // Update validation when form data changes
    useEffect(() => {
        setFormState(prev => ({
            ...prev,
            isStepValid: {
                1: validateStep1(prev.step1),
                2: prev.isStepValid[2]
            }
        }));
    }, [formState.step1]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formState.isStepValid[1]) {
            return;
        }

        setIsSaving(true);

        try {
            const companyData = {
                company_name: formState.step1.company_name,
                company_type: formState.step1.company_type as CompanyType,
                legal_name: formState.step1.legal_name,
                tax_id: formState.step1.tax_id,
                address_line1: formState.step1.address.street,
                city: formState.step1.address.city,
                state_province: formState.step1.address.state,
                postal_code: formState.step1.address.postalCode,
                country_code: formState.step1.country_code,
                primary_email: formState.step1.primary_email,
                primary_phone: formState.step1.primary_phone || undefined,
                is_active: formState.step1.is_active,
                notes: formState.step1.notes || undefined,
            };

            if (isEdit && id) {
                await updateCompany(id, companyData);
            } else {
                // Create with team
                await createCompanyWithTeam({
                    ...companyData,
                    team_members: formState.step2.team_members
                });
            }

            navigate('/admin/companies');
        } catch (error) {
            console.error('Error saving company:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        navigate('/admin/companies');
    };

    // Stepper navigation
    const handleNextStep = () => {
        if (formState.currentStep === 1 && formState.isStepValid[1]) {
            setFormState(prev => ({ ...prev, currentStep: 2 }));
        }
    };

    const handlePreviousStep = () => {
        if (formState.currentStep === 2) {
            setFormState(prev => ({ ...prev, currentStep: 1 }));
        }
    };

    const handleStepChange = (step: number) => {
        setFormState(prev => ({ ...prev, currentStep: step }));
    };

    // Step 1 handlers
    const updateStep1 = (updates: Partial<CompanyFormStep1Data>) => {
        setFormState(prev => ({
            ...prev,
            step1: { ...prev.step1, ...updates }
        }));
    };

    const updateStep1Address = (updates: Partial<CompanyFormStep1Data['address']>) => {
        setFormState(prev => ({
            ...prev,
            step1: {
                ...prev.step1,
                address: { ...prev.step1.address, ...updates }
            }
        }));
    };

    // Step 2 handlers - Team Management
    const handleAddTeamMember = () => {
        if (!newMemberEmail.trim()) {
            return;
        }

        // Check for duplicate email
        if (formState.step2.team_members.some(m => m.email.toLowerCase() === newMemberEmail.toLowerCase())) {
            alert(t('form.stepper.step2.duplicateEmail'));
            return;
        }

        const newMember: TeamMember = {
            email: newMemberEmail.trim(),
            first_name: newMemberFirstName.trim() || undefined,
            last_name: newMemberLastName.trim() || undefined,
            role: newMemberRole
        };

        setFormState(prev => ({
            ...prev,
            step2: {
                team_members: [...prev.step2.team_members, newMember]
            }
        }));

        // Reset form
        setNewMemberEmail('');
        setNewMemberFirstName('');
        setNewMemberLastName('');
        setNewMemberRole('operator');
    };

    const handleRemoveTeamMember = (index: number) => {
        setFormState(prev => ({
            ...prev,
            step2: {
                team_members: prev.step2.team_members.filter((_, i) => i !== index)
            }
        }));
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">{t('form.loading')}</p>
                </div>
            </div>
        );
    }

    const stepConfigs = [
        {
            title: t('form.stepper.step1.title'),
            description: t('form.stepper.step1.description')
        },
        {
            title: t('form.stepper.step2.title'),
            description: t('form.stepper.step2.description')
        }
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button
                        onClick={handleCancel}
                        size="sm"
                        color="secondary"
                        iconLeading={ArrowLeft}
                    >
                        {t('form.back')}
                    </Button>
                    <div>
                        <h1 className="text-2xl font-semibold text-primary">
                            {isEdit ? t('form.edit.title') : t('form.create.title')}
                        </h1>
                        <p className="text-tertiary mt-1">
                            {isEdit ? t('form.edit.subtitle') : t('form.create.subtitle')}
                        </p>
                    </div>
                </div>
                {isEdit && id && (
                    <Link
                        to={`/admin/companies/${id}/menu-config`}
                        className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover"
                    >
                        <Settings01 className="size-4" />
                        {t('form.actions.menuConfig')}
                    </Link>
                )}
            </div>

            {/* Stepper Form */}
            <form onSubmit={handleSubmit}>
                <Stepper
                    currentStep={formState.currentStep}
                    steps={stepConfigs}
                    completedSteps={[]}
                    validSteps={Object.keys(formState.isStepValid).filter(step => formState.isStepValid[parseInt(step)]).map(Number)}
                    onStepChange={handleStepChange}
                    allowStepNavigation={true}
                >
                    {/* Step 1: Company Info */}
                    <StepperStep
                        stepNumber={1}
                        title={t('form.stepper.step1.title')}
                        isActive={formState.currentStep === 1}
                        isCompleted={formState.isStepValid[1]}
                        isValid={formState.isStepValid[1]}
                    >
                        <div className="space-y-6">
                            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                                <h2 className="text-lg font-semibold text-primary mb-6">{t('form.sections.general')}</h2>

                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                    <Select
                                        label={t('form.fields.companyType')}
                                        placeholder={t('form.fields.companyTypePlaceholder')}
                                        items={companyTypeOptions}
                                        selectedKey={formState.step1.company_type}
                                        onSelectionChange={(key) => updateStep1({ company_type: key as CompanyType })}
                                        isRequired
                                        size="md"
                                    >
                                        {(item) => (
                                            <Select.Item key={item.id} id={item.id}>
                                                {item.label}
                                            </Select.Item>
                                        )}
                                    </Select>

                                    <Input
                                        label={t('form.fields.companyName')}
                                        placeholder={t('form.fields.companyNamePlaceholder')}
                                        value={formState.step1.company_name}
                                        onChange={(value) => updateStep1({ company_name: value })}
                                        isRequired
                                        size="md"
                                    />

                                    <Input
                                        label={t('form.fields.legalName')}
                                        placeholder={t('form.fields.legalNamePlaceholder')}
                                        value={formState.step1.legal_name}
                                        onChange={(value) => updateStep1({ legal_name: value })}
                                        isRequired
                                        size="md"
                                    />

                                    <Input
                                        label={t('form.fields.taxId')}
                                        placeholder={t('form.fields.taxIdPlaceholder')}
                                        value={formState.step1.tax_id}
                                        onChange={(value) => updateStep1({ tax_id: value })}
                                        isRequired
                                        size="md"
                                    />

                                    <Select
                                        label={t('form.fields.status')}
                                        placeholder={t('form.fields.statusPlaceholder')}
                                        items={statusOptions}
                                        selectedKey={String(formState.step1.is_active)}
                                        onSelectionChange={(key) => updateStep1({ is_active: key === 'true' })}
                                        isRequired
                                        size="md"
                                    >
                                        {(item) => (
                                            <Select.Item key={item.id} id={item.id}>
                                                {item.label}
                                            </Select.Item>
                                        )}
                                    </Select>
                                </div>
                            </div>

                            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                                <h2 className="text-lg font-semibold text-primary mb-6">{t('form.sections.address')}</h2>

                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                    <div className="sm:col-span-2">
                                        <Input
                                            label={t('form.fields.street')}
                                            placeholder={t('form.fields.streetPlaceholder')}
                                            value={formState.step1.address.street}
                                            onChange={(value) => updateStep1Address({ street: value })}
                                            isRequired
                                            size="md"
                                        />
                                    </div>

                                    <Input
                                        label={t('form.fields.city')}
                                        placeholder={t('form.fields.cityPlaceholder')}
                                        value={formState.step1.address.city}
                                        onChange={(value) => updateStep1Address({ city: value })}
                                        isRequired
                                        size="md"
                                    />

                                    <Input
                                        label={t('form.fields.state')}
                                        placeholder={t('form.fields.statePlaceholder')}
                                        value={formState.step1.address.state}
                                        onChange={(value) => updateStep1Address({ state: value })}
                                        isRequired
                                        size="md"
                                    />

                                    <Input
                                        label={t('form.fields.postalCode')}
                                        placeholder={t('form.fields.postalCodePlaceholder')}
                                        value={formState.step1.address.postalCode}
                                        onChange={(value) => updateStep1Address({ postalCode: value })}
                                        isRequired
                                        size="md"
                                    />

                                    <Select
                                        label={t('form.fields.country')}
                                        placeholder={t('form.fields.countryPlaceholder')}
                                        items={countryOptions}
                                        selectedKey={formState.step1.country_code}
                                        onSelectionChange={(key) => updateStep1({ country_code: key as string })}
                                        isRequired
                                        size="md"
                                    >
                                        {(item) => (
                                            <Select.Item key={item.id} id={item.id}>
                                                {item.label}
                                            </Select.Item>
                                        )}
                                    </Select>
                                </div>
                            </div>

                            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                                <h2 className="text-lg font-semibold text-primary mb-6">{t('form.sections.contacts')}</h2>

                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                    <Input
                                        label={t('form.fields.email')}
                                        type="email"
                                        placeholder={t('form.fields.emailPlaceholder')}
                                        value={formState.step1.primary_email}
                                        onChange={(value) => updateStep1({ primary_email: value })}
                                        isRequired
                                        size="md"
                                    />

                                    <Input
                                        label={t('form.fields.phone')}
                                        placeholder={t('form.fields.phonePlaceholder')}
                                        value={formState.step1.primary_phone}
                                        onChange={(value) => updateStep1({ primary_phone: value })}
                                        size="md"
                                    />
                                </div>
                            </div>

                            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                                <h2 className="text-lg font-semibold text-primary mb-6">{t('form.sections.notes')}</h2>

                                <TextArea
                                    label={t('form.fields.notes')}
                                    placeholder={t('form.fields.notesPlaceholder')}
                                    value={formState.step1.notes}
                                    onChange={(value) => updateStep1({ notes: value })}
                                    rows={4}
                                />
                            </div>
                        </div>
                    </StepperStep>

                    {/* Step 2: Team Management */}
                    <StepperStep
                        stepNumber={2}
                        title={t('form.stepper.step2.title')}
                        description={t('form.stepper.step2.description')}
                        isActive={formState.currentStep === 2}
                        isCompleted={formState.isStepValid[2]}
                        isValid={formState.isStepValid[2]}
                    >
                        <div className="space-y-6">
                            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                                <h2 className="text-lg font-semibold text-primary mb-6">{t('form.stepper.step2.title')}</h2>

                                {/* Add Team Member Form */}
                                <div className="mb-6 p-4 bg-secondary/5 rounded-lg border border-secondary/10">
                                    <h3 className="text-sm font-semibold text-secondary mb-4">{t('form.stepper.step2.addMember')}</h3>
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                                        <Input
                                            label={t('form.stepper.step2.fields.email')}
                                            type="email"
                                            placeholder={t('form.stepper.step2.fields.emailPlaceholder')}
                                            value={newMemberEmail}
                                            onChange={setNewMemberEmail}
                                            isRequired={formState.step2.team_members.length === 0}
                                            size="md"
                                        />
                                        <Input
                                            label={t('form.stepper.step2.fields.firstName')}
                                            placeholder={t('form.stepper.step2.fields.firstNamePlaceholder')}
                                            value={newMemberFirstName}
                                            onChange={setNewMemberFirstName}
                                            size="md"
                                        />
                                        <Input
                                            label={t('form.stepper.step2.fields.lastName')}
                                            placeholder={t('form.stepper.step2.fields.lastNamePlaceholder')}
                                            value={newMemberLastName}
                                            onChange={setNewMemberLastName}
                                            size="md"
                                        />
                                        <div className="flex items-end">
                                            <Select
                                                label={t('form.stepper.step2.fields.role')}
                                                placeholder={t('form.stepper.step2.fields.rolePlaceholder')}
                                                items={roleOptions}
                                                selectedKey={newMemberRole}
                                                onSelectionChange={(key) => setNewMemberRole(key as UserRole)}
                                                size="md"
                                                className="flex-1"
                                            >
                                                {(item) => (
                                                    <Select.Item key={item.id} id={item.id}>
                                                        {item.label}
                                                    </Select.Item>
                                                )}
                                            </Select>
                                        </div>
                                    </div>
                                    <div className="mt-4">
                                        <Button
                                            type="button"
                                            onClick={handleAddTeamMember}
                                            size="sm"
                                            color="primary"
                                            iconLeading={Plus}
                                            isDisabled={!newMemberEmail.trim()}
                                        >
                                            {t('form.stepper.step2.addButton')}
                                        </Button>
                                    </div>
                                </div>

                                {/* Team Members List */}
                                {formState.step2.team_members.length > 0 && (
                                    <div>
                                        <h3 className="text-sm font-semibold text-secondary mb-4">
                                            {t('form.stepper.step2.membersList')} ({formState.step2.team_members.length})
                                        </h3>
                                        <div className="space-y-2">
                                            {formState.step2.team_members.map((member, index) => (
                                                <div
                                                    key={index}
                                                    className="flex items-center justify-between p-3 bg-secondary/5 rounded-lg border border-secondary/10"
                                                >
                                                    <div className="flex-1">
                                                        <div className="font-medium text-primary">
                                                            {member.first_name && member.last_name
                                                                ? `${member.first_name} ${member.last_name}`
                                                                : member.email}
                                                        </div>
                                                        <div className="text-sm text-tertiary">{member.email}</div>
                                                        <div className="text-xs text-quaternary">
                                                            {t('form.stepper.step2.roles.' + member.role)}
                                                        </div>
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        onClick={() => handleRemoveTeamMember(index)}
                                                        size="sm"
                                                        color="tertiary-destructive"
                                                        iconLeading={Trash01}
                                                    >
                                                        {t('form.stepper.step2.remove')}
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {formState.step2.team_members.length === 0 && (
                                    <div className="text-center py-8 text-tertiary">
                                        <p>{t('form.stepper.step2.noMembers')}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </StepperStep>
                </Stepper>

                {/* Form Actions */}
                <div className="flex items-center justify-between mt-8">
                    <div>
                        {formState.currentStep > 1 && (
                            <Button
                                type="button"
                                onClick={handlePreviousStep}
                                size="md"
                                color="secondary"
                                iconLeading={ChevronLeft}
                            >
                                {t('form.actions.previous')}
                            </Button>
                        )}
                    </div>

                    <div className="flex items-center gap-4">
                        <Button
                            type="button"
                            onClick={handleCancel}
                            size="md"
                            color="secondary"
                            iconLeading={X}
                        >
                            {t('form.actions.cancel')}
                        </Button>

                        {formState.currentStep < 2 ? (
                            <Button
                                type="button"
                                onClick={handleNextStep}
                                size="md"
                                color="primary"
                                iconTrailing={ChevronRight}
                                isDisabled={!formState.isStepValid[formState.currentStep]}
                            >
                                {t('form.actions.next')}
                            </Button>
                        ) : (
                            <Button
                                type="submit"
                                size="md"
                                color="primary"
                                iconLeading={Save01}
                                isLoading={isSaving}
                                isDisabled={!formState.isStepValid[1]}
                            >
                                {isEdit ? t('form.actions.save') : t('form.actions.create')}
                            </Button>
                        )}
                    </div>
                </div>
            </form>

            {/* Loading Modal */}
            <ModalOverlay isOpen={isSaving} onOpenChange={() => {}}>
                <Modal>
                    <Dialog>
                        <div className="rounded-xl bg-primary p-8 shadow-lg ring-1 ring-secondary">
                            <div className="flex flex-col items-center gap-4">
                                <LoadingIndicator type="line-spinner" size="lg" />
                                <p className="text-lg font-semibold text-primary">
                                    {isEdit ? t('form.saving') : t('form.creating')}
                                </p>
                            </div>
                        </div>
                    </Dialog>
                </Modal>
            </ModalOverlay>
        </div>
    );
};

